/**
 * Coach Suggestions Panel — actionable one-click cards.
 *
 * Renders the output of buildSuggestions as a list of cards with
 * "Apply" + "Dismiss" buttons. Clicking Apply dispatches the underlying
 * Action via the parent's coachActionDispatcher (existing pipeline);
 * Dismiss records a rejection so Coach stops nagging. Both decisions
 * persist via the parent's settings.coachSuggestions slice.
 *
 * Keeps the dashboard's "Coach observed" list pure (no Apply buttons
 * cluttering the analyzer cards) — Suggestions panel is the place where
 * the trader takes action, observation cards are where they read.
 *
 * Props:
 *   suggestions   Array<Suggestion> — pending list from buildSuggestions
 *   onApply       (suggestion) => Promise|void — dispatch the action + record accept
 *   onDismiss     (suggestion) => void — record rejection
 *   darkMode      bool
 */

import { useState } from 'react';
import { Lightbulb, Check, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const ACTION_LABEL = {
  'pin-confluence-to-setup': 'Pin confluence to setup',
  'add-rule-to-setup':       'Add rule to setup',
  'add-lesson-to-session':   'Add lesson',
  'tag-trade-mistake':       'Tag mistake on trades',
};

const SuggestionCard = ({ suggestion, onApply, onDismiss, darkMode }) => {
  const [busy, setBusy] = useState(null);  // null | 'applying' | 'dismissing'
  const [expanded, setExpanded] = useState(false);

  const handleApply = async () => {
    if (busy) return;
    setBusy('applying');
    try { await onApply(suggestion); }
    finally { setBusy(null); }
  };
  const handleDismiss = () => {
    if (busy) return;
    setBusy('dismissing');
    try { onDismiss(suggestion); }
    finally { setBusy(null); }
  };

  const sevAccent = {
    success:  darkMode ? 'border-l-emerald-500' : 'border-l-emerald-500',
    warning:  darkMode ? 'border-l-amber-500'   : 'border-l-amber-500',
    critical: darkMode ? 'border-l-red-500'     : 'border-l-red-500',
    info:     darkMode ? 'border-l-sky-500'     : 'border-l-sky-500',
  }[suggestion.severity] || 'border-l-purple-500';

  const actionLabel = suggestion.action?.label || ACTION_LABEL[suggestion.action?.type] || 'Apply';

  return (
    <div className={`rounded-lg border-l-4 ${sevAccent} p-3 ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {suggestion.scope}
            {suggestion.scopeId && <span className="opacity-70"> · {suggestion.scopeId}</span>}
          </div>
          <p className={`text-sm font-medium leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {suggestion.title}
          </p>
        </div>
      </div>

      {/* Detail (collapsed by default to keep the panel scannable) */}
      {suggestion.detail && (
        <div className="mt-1.5">
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className={`text-[10px] inline-flex items-center gap-0.5 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {expanded ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
            {expanded ? 'Hide evidence' : 'Show evidence'}
          </button>
          {expanded && (
            <p className={`text-xs leading-snug mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {suggestion.detail}
            </p>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-1.5 mt-2">
        <button
          type="button"
          onClick={handleDismiss}
          disabled={!!busy}
          className={`text-[11px] px-2 py-1 rounded-md border inline-flex items-center gap-1 ${
            darkMode
              ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.09]'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          } ${busy ? 'opacity-60 cursor-wait' : ''}`}
          title="Dismiss this suggestion (Coach won't nag again)"
        >
          {busy === 'dismissing' ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
          Dismiss
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={!!busy}
          className={`text-[11px] px-2.5 py-1 rounded-md font-medium inline-flex items-center gap-1 ${
            darkMode
              ? 'bg-purple-500/30 border border-purple-400/50 text-purple-100 hover:bg-purple-500/40'
              : 'bg-purple-100 border border-purple-300 text-purple-800 hover:bg-purple-200'
          } ${busy ? 'opacity-60 cursor-wait' : ''}`}
          title="Apply this suggestion immediately"
        >
          {busy === 'applying' ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
          {actionLabel}
        </button>
      </div>
    </div>
  );
};

const CoachSuggestionsPanel = ({
  suggestions = [],
  onApply,
  onDismiss,
  darkMode = true,
}) => {
  if (!Array.isArray(suggestions) || suggestions.length === 0) return null;

  return (
    <div className={`rounded-xl border p-4 ${darkMode ? 'bg-purple-500/5 border-purple-500/20' : 'bg-purple-50/40 border-purple-200'}`}>
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={14} className={darkMode ? 'text-purple-300' : 'text-purple-700'} />
        <h3 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
          Coach Suggestions
        </h3>
        <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          · {suggestions.length} pending
        </span>
      </div>
      <div className="space-y-2">
        {suggestions.map(s => (
          <SuggestionCard
            key={s.key}
            suggestion={s}
            onApply={onApply}
            onDismiss={onDismiss}
            darkMode={darkMode}
          />
        ))}
      </div>
    </div>
  );
};

export default CoachSuggestionsPanel;
